/**
 * Tile model for the neutral classical 4-player set (136 tiles, no flowers, no jokers).
 *
 * Tile kinds use verbose, self-describing codes so that serialized state,
 * replays and logs stay readable without a lookup table.
 */

export const CHARACTER_KINDS = [
  "CHA1", "CHA2", "CHA3", "CHA4", "CHA5", "CHA6", "CHA7", "CHA8", "CHA9",
] as const;

export const BAMBOO_KINDS = [
  "BAM1", "BAM2", "BAM3", "BAM4", "BAM5", "BAM6", "BAM7", "BAM8", "BAM9",
] as const;

export const DOT_KINDS = [
  "DOT1", "DOT2", "DOT3", "DOT4", "DOT5", "DOT6", "DOT7", "DOT8", "DOT9",
] as const;

export const WIND_KINDS = ["WIND_EAST", "WIND_SOUTH", "WIND_WEST", "WIND_NORTH"] as const;

export const DRAGON_KINDS = ["DRAGON_RED", "DRAGON_GREEN", "DRAGON_WHITE"] as const;

export const HONOR_KINDS = [...WIND_KINDS, ...DRAGON_KINDS] as const;

export const SUITED_KINDS = [...CHARACTER_KINDS, ...BAMBOO_KINDS, ...DOT_KINDS] as const;

/** Canonical ordering. Index in this array is the sort order used everywhere. */
export const TILE_KINDS = [...SUITED_KINDS, ...HONOR_KINDS] as const;

export type CharacterKind = (typeof CHARACTER_KINDS)[number];
export type BambooKind = (typeof BAMBOO_KINDS)[number];
export type DotKind = (typeof DOT_KINDS)[number];
export type WindKind = (typeof WIND_KINDS)[number];
export type DragonKind = (typeof DRAGON_KINDS)[number];
export type HonorKind = (typeof HONOR_KINDS)[number];
export type SuitedKind = (typeof SUITED_KINDS)[number];
export type TileKind = (typeof TILE_KINDS)[number];

export type NumberSuit = "characters" | "bamboo" | "dots";
export type Suit = NumberSuit | "winds" | "dragons";

/** How many identical copies of each kind exist in the set. */
export const COPIES_PER_KIND = 4;
/** 34 kinds x 4 copies. */
export const TILES_IN_SET = TILE_KINDS.length * COPIES_PER_KIND;

export interface TileInfo {
  kind: TileKind;
  suit: Suit;
  /** 1..9 for suited tiles, 0 for honors. */
  rank: number;
  isHonor: boolean;
  /** 1 or 9 of a numbered suit. */
  isTerminal: boolean;
  /** Neutral English name; UI layers localise on top of this. */
  name: string;
}

/** A physical tile in a game. `id` is unique within one game (0..135). */
export interface Tile {
  id: number;
  kind: TileKind;
}

const HONOR_NAMES: Record<HonorKind, string> = {
  WIND_EAST: "East Wind",
  WIND_SOUTH: "South Wind",
  WIND_WEST: "West Wind",
  WIND_NORTH: "North Wind",
  DRAGON_RED: "Red Dragon",
  DRAGON_GREEN: "Green Dragon",
  DRAGON_WHITE: "White Dragon",
};

const SUITED_NAMES: Record<NumberSuit, string> = {
  characters: "Characters",
  bamboo: "Bamboo",
  dots: "Dots",
};

function buildTileInfo(): Record<TileKind, TileInfo> {
  const table = {} as Record<TileKind, TileInfo>;
  for (const kind of TILE_KINDS) {
    let suit: Suit;
    let rank = 0;
    if (kind.startsWith("CHA")) suit = "characters";
    else if (kind.startsWith("BAM")) suit = "bamboo";
    else if (kind.startsWith("DOT")) suit = "dots";
    else if (kind.startsWith("WIND")) suit = "winds";
    else suit = "dragons";

    const isHonor = suit === "winds" || suit === "dragons";
    if (!isHonor) rank = Number(kind.slice(3));

    table[kind] = {
      kind,
      suit,
      rank,
      isHonor,
      isTerminal: !isHonor && (rank === 1 || rank === 9),
      name: isHonor
        ? HONOR_NAMES[kind as HonorKind]
        : `${rank} ${SUITED_NAMES[suit as NumberSuit]}`,
    };
  }
  return table;
}

export const TILE_INFO: Record<TileKind, TileInfo> = buildTileInfo();

const KIND_INDEX: Record<TileKind, number> = (() => {
  const table = {} as Record<TileKind, number>;
  TILE_KINDS.forEach((kind, index) => {
    table[kind] = index;
  });
  return table;
})();

/** Position of a kind in the canonical ordering (0..33). */
export function kindIndex(kind: TileKind): number {
  return KIND_INDEX[kind];
}

export function kindAtIndex(index: number): TileKind {
  const kind = TILE_KINDS[index];
  if (kind === undefined) throw new RangeError(`No tile kind at index ${index}`);
  return kind;
}

export function isHonor(kind: TileKind): boolean {
  return TILE_INFO[kind].isHonor;
}

export function isTerminal(kind: TileKind): boolean {
  return TILE_INFO[kind].isTerminal;
}

/** Ordered 136 tile set, ids 0..135, before shuffling. */
export function buildTileSet(): Tile[] {
  const tiles: Tile[] = [];
  let id = 0;
  for (const kind of TILE_KINDS) {
    for (let copy = 0; copy < COPIES_PER_KIND; copy += 1) {
      tiles.push({ id, kind });
      id += 1;
    }
  }
  return tiles;
}

export function compareTiles(a: Tile, b: Tile): number {
  const byKind = kindIndex(a.kind) - kindIndex(b.kind);
  return byKind !== 0 ? byKind : a.id - b.id;
}

/** New sorted array; input untouched. */
export function sortTiles(tiles: readonly Tile[]): Tile[] {
  return tiles.slice().sort(compareTiles);
}

export function toKinds(tiles: readonly Tile[]): TileKind[] {
  return tiles.map((tile) => tile.kind);
}

/** Count array of length 34 indexed by `kindIndex`. */
export function countKinds(kinds: readonly TileKind[]): number[] {
  const counts = new Array<number>(TILE_KINDS.length).fill(0);
  for (const kind of kinds) {
    const index = kindIndex(kind);
    counts[index] = (counts[index] ?? 0) + 1;
  }
  return counts;
}
